<script lang="ts">
import VisualisationCanvas from '@/components/public/Visualisations/VisualisationCanvas.vue'
import Component from 'vue-class-component'
import { LeafNode, TreeNode, XYBubblesConfig, XYDimensionsMapping } from '@/types'
import XYBubblesSimulation from '@/components/public/Visualisations/XYBubbles/XYBubblesSimulation'
import { shouldBubblesAmalgamate } from '@/components/public/Visualisations/BubblesV2/bubblesVisualisationHelpersV2'
import { EntitiesRegister } from '@/components/public/Visualisations/BubblesV2/Entities'
import { Shapes } from '@/components/public/Visualisations/types'
import { XYBubbleMetadata } from '@/components/public/Visualisations/XYBubbles/Metadata/XYBubbleMetadata'
import { XYBubblesMetadataRegister } from '@/components/public/Visualisations/XYBubbles/Metadata/XYBubblesMetadataRegister'
import { XYBubblesTransitionOperations } from '@/components/public/Visualisations/XYBubbles/XYBubblesTransitionOperations'
import { XYBubbleGroup } from '@/components/public/Visualisations/XYBubbles/Entities/XYBubbleGroup'
import { XYVisNode } from '@/components/public/Visualisations/XYBubbles/types'
import { iterateShades } from '@/helpers/shadeIterator'
import { XAxisGroup } from '@/components/public/Visualisations/XYBubbles/Entities/Axis/XAxisGroup'
import { YAxisGroup } from '@/components/public/Visualisations/XYBubbles/Entities/Axis/YAxisGroup'
import { settingsService } from '@/services'
import { BoundingBoxRect } from '@/components/public/Visualisations/Debugging'
import { xyBubblesLayoutCalculator } from './xyBubblesLayoutCalculator'
import logger from '@/plugins/logging/logger'
import _ from 'lodash'
import Konva from 'konva'

interface BubbleAmalgamation {
  ancestor: XYBubbleMetadata
  descendants: XYBubbleMetadata[]
}

// eslint-disable-next-line no-use-before-define
@Component<XYBubblesCanvas>({
  watch: {
    storyPath() {
      this.storyPathChanged()
    },
    visNodes() {
      this.nodesUpdated()
    },
    config: {
      handler() {
        this.updateLayout()
      },
      deep: true,
    },
    initialViewBoundingRect() {
      this.updateLayout()
    },
    userPanOffset: {
      handler() {
        this.updateAxesHorizontalOffsets()
      },
      deep: true,
    },
  },
})
export default class XYBubblesCanvas extends VisualisationCanvas {
  private simulation: XYBubblesSimulation = new XYBubblesSimulation()
  private lastKnownDimensionsMappings: XYDimensionsMapping[] = []
  private metadataRegister: XYBubblesMetadataRegister = new XYBubblesMetadataRegister()
  private entitiesRegister: EntitiesRegister = new EntitiesRegister()
  private pendingBubbleAmalgamations: BubbleAmalgamation[] = []
  private pendingBubblesToRemove: XYBubbleMetadata[] = []
  private simulationEndedBubbles: XYBubbleMetadata[] = []
  private xAxis: XAxisGroup = new XAxisGroup()
  private yAxis: YAxisGroup = new YAxisGroup()
  private axesBoundingBox: BoundingBoxRect = new BoundingBoxRect('green')
  private bubblesBoundingBox: BoundingBoxRect = new BoundingBoxRect('blue')
  private visualisationBoundingBox: BoundingBoxRect = new BoundingBoxRect('purple')

  mounted() {
    this.initialiseSimulation()
    this.updateBubbles()
    this.updateSimulation()

    this.addGroup(this.xAxis)
    this.addGroup(this.yAxis)
    this.addShape(this.axesBoundingBox)
    this.axesBoundingBox.visible(settingsService.bubblesCanvasDebugMode)
    this.addShape(this.bubblesBoundingBox)
    this.bubblesBoundingBox.visible(settingsService.bubblesCanvasDebugMode)
    this.addShape(this.visualisationBoundingBox)
    this.visualisationBoundingBox.visible(settingsService.bubblesCanvasDebugMode)

    this.loading = false
  }

  get config(): XYBubblesConfig {
    return this.$typedStore.visualisationApp.xyBubblesConfig
  }

  get showZeroValueBubbles() {
    return this.$typedStore.visualisationApp.bubbleParams.showZeroValueBubbles
  }

  get dimensionsMappings(): XYDimensionsMapping[] {
    return this.$typedStore.visualisationApp.xyDimensionsMappings
  }

  get visualisationData() {
    return this.$typedStore.visualisationData
  }

  get storyPath() {
    return this.visualisationData.storyPath
  }

  get storyPathTree() {
    return this.visualisationData.filteredTreeRoot.search(this.storyPath)
  }

  get visNodes(): XYVisNode[] {
    const result: XYVisNode[] = []

    if (this.dimensionsMappings !== this.lastKnownDimensionsMappings) {
      this.removeBubblesFromCanvas(this.metadataRegister.bubbles)
      this.removeBubblesFromCanvas(this.pendingBubblesToRemove)
      this.lastKnownDimensionsMappings = this.dimensionsMappings
    }

    if (this.storyPathTree) {
      if (this.storyPathTree.children) {
        this.storyPathTree.children.forEach((child) => {
          const visNodes = this.buildVisNodes(child)
          result.push(...visNodes)
        })
      } else {
        result.push(...this.buildVisNodes(this.storyPathTree))
      }
    }

    return result
  }

  private buildVisNodes(node: TreeNode): XYVisNode[] {
    const result: XYVisNode[] = []

    this.dimensionsMappings.forEach((mapping) => {
      const { xAxisTickKey, yAxisColumn, bubbleSizeColumn } = mapping
      if (!xAxisTickKey || !yAxisColumn || !bubbleSizeColumn) return

      const leafNodes = this.visualisationData.getLeafData(node)
      let visNodeKey = `${node.path.toString()}_${xAxisTickKey}`

      if (this.config.yAxisTicks.useDistinctValues) {
        this.config.yAxisTicks.customLabels.forEach((label) => {
          const yTickLeafNodes = leafNodes.filter((leafNode) => {
            const yAxisValue = parseInt(leafNode.data[yAxisColumn])
            return yAxisValue === label.value
          })
          visNodeKey = `${visNodeKey}_${label.value}`
          const visNode = this.buildVisNode(visNodeKey, node, yTickLeafNodes, mapping, label.value)
          if (visNode) result.push(visNode)
        })
      } else {
        const visNode = this.buildVisNode(visNodeKey, node, leafNodes, mapping)
        if (visNode) {
          result.push(visNode)
        }
      }
    })

    return result
  }

  private buildVisNode(
    key: string,
    node: TreeNode,
    leafNodes: LeafNode[],
    mapping: XYDimensionsMapping,
    distinctYAxisValue?: number,
  ) {
    const { xAxisTickKey, yAxisColumn, bubbleSizeColumn } = mapping
    const visNode = this.visualisationData.buildVisNodeFromLeafNodes(
      leafNodes,
      node.name,
      node.path,
    ) as XYVisNode
    visNode.key = key
    visNode.xAxisTickKey = xAxisTickKey
    visNode.yAxisValue = distinctYAxisValue ?? 0
    visNode.total = 0

    leafNodes.forEach((leaf) => {
      if (!distinctYAxisValue) {
        const yAxisValue = parseInt(leaf.data[yAxisColumn])

        if (isNaN(yAxisValue)) {
          logger.error(
            `data in [${yAxisColumn}] column not a number for path [${leaf.path.toString()}]`,
          )
          return
        }
        visNode.yAxisValue += yAxisValue
      }

      const bubbleTotal = parseInt(leaf.data[bubbleSizeColumn])
      if (isNaN(bubbleTotal)) {
        logger.error(
          `data in [${bubbleSizeColumn}] column not a number for path [${leaf.path.toString()}]`,
        )
        return
      }

      visNode.total += bubbleTotal
    })

    if (!this.showZeroValueBubbles && visNode.total === 0) return

    const scaleFactor = this.visualisationData.level1Configs[node.path.level1].scaleFactor
    visNode.scaledTotal = scaleFactor * visNode.total
    visNode.colour = this.visNodeColoursByPath[visNode.path.toString()] || visNode.colour

    return visNode
  }

  get visNodeColoursByPath() {
    const colours: Record<string, string> = {}
    const { storyPathTree, visualisationData } = this

    if (storyPathTree && storyPathTree.children && storyPathTree.children.length > 1) {
      const level1Path = storyPathTree.path.level1
      const baseColour = visualisationData.level1Configs[level1Path]?.colour

      if (baseColour) {
        const shadeIterator = iterateShades(baseColour, storyPathTree.children.length)
        storyPathTree.children.forEach((child) => {
          colours[child.path.toString()] = shadeIterator.next()
        })
      }
    }

    return colours
  }

  private initialiseSimulation() {
    this.simulation.on('tick', this.simulationTicked)
    this.simulation.on('end', this.simulationEnded)
  }

  private simulationTicked() {
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      if (shouldBubblesAmalgamate(bubbleAmalgamation.descendants, this.minimumCanvasSideLength)) {
        this.amalgamateBubbles(bubbleAmalgamation)
      }
    }
  }

  private simulationEnded() {
    // Amalgamate any Bubbles that should have already been amalgamated but haven't for whatever reason
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      this.amalgamateBubbles(bubbleAmalgamation)
    }
    // Remove any Bubbles that should have already been removed but haven't
    this.removeBubblesFromCanvas(this.pendingBubblesToRemove)
    this.pendingBubblesToRemove = []
  }

  private storyPathChanged() {
    this.pendingBubblesToRemove = []
  }

  private nodesUpdated() {
    this.updateBubbles()
    this.refreshEntities()
    this.updateSimulation()
  }

  private refreshEntities() {
    this.entitiesRegister.allEntities().forEach((entity: Shapes.Entity) => {
      entity.refresh()
    })
  }

  private updateBubbles() {
    const bubblesOperations = new XYBubblesTransitionOperations()
    const bubblesKeysToRemove = new Set(this.metadataRegister.bubbles.map((x) => x.key))

    this.updateMainBubbles(bubblesOperations, bubblesKeysToRemove)

    bubblesKeysToRemove.forEach((key) => {
      const bubble = this.metadataRegister.getBubble(key)
      if (bubble) {
        bubblesOperations.appendBubbleToRemove(bubble)
      }
    })
    if (bubblesOperations.hasOperations) {
      const bubblesToRemoveSet = new Set(bubblesOperations.bubblesToRemoveValues)
      const persistingBubbles = this.metadataRegister.bubbles.filter(
        (bubble) => !bubblesToRemoveSet.has(bubble),
      )
      this.simulationEndedBubbles = bubblesOperations.bubblesToAddValues.concat(persistingBubbles)

      this.simulationEndedBubbles
        .sort((a, b) => b.radius - a.radius)
        .forEach((bubble, index) => {
          bubble.zIndex = index
        })

      this.updateLayout()

      this.addBubblesToCanvas(bubblesOperations.toAdd)
      this.removeBubblesFromCanvas(bubblesOperations.toRemove)

      bubblesOperations.amalgamating.forEach((op) =>
        this.handleBubbleAmalgamating(op.add, op.remove),
      )
      bubblesOperations.splitting.forEach((op) => this.handleBubbleSplitting(op.add, op.remove))
    }
  }

  private updateMainBubbles(
    bubblesOperations: XYBubblesTransitionOperations,
    bubblesKeysToRemove: Set<string>,
  ) {
    this.visNodes.forEach((node) => {
      let bubbleMetadata = this.metadataRegister.getBubble(node.key)
      if (bubbleMetadata) {
        bubbleMetadata.updateNode(node)
      } else {
        bubbleMetadata = new XYBubbleMetadata(node, this.canvasCenter)
        bubblesOperations.appendBubbleToAdd(bubbleMetadata)
      }
      bubblesKeysToRemove.delete(node.key)
    })
  }

  private handleBubbleSplitting(
    descendantBubbles: XYBubbleMetadata[],
    bubbleToSplit: XYBubbleMetadata,
  ) {
    if (descendantBubbles.length) {
      // Determine the destination of the first descendant Bubble then update the destination
      // position for all of the Bubbles, including the Bubble that is going to be split
      descendantBubbles.forEach((descendant: XYBubbleMetadata) => {
        descendant.x = bubbleToSplit.x
        descendant.y = bubbleToSplit.y
      })

      this.addBubblesToCanvas(descendantBubbles)
      this.removeBubblesFromCanvas([bubbleToSplit])

      this.updateSimulation()
    }
  }

  /*
   * When descendant bubbles are amalgamating into their common ancestor we want
   * the descendant bubbles to move towards the new ancestor position then
   * when enough of them are close to that position replace the descendant bubbles
   * with the ancestor bubble, this should give the illusion that the bubbles are merging together.
   * To achieve this we create the ancestor bubble but do not render it yet. We then
   * set the descendant bubbles' destination positions to the ancestor bubble's
   * destination position and update the simulation to start moving the descendant bubbles
   * together.
   * As the simulation ticks we then calculate whether enough of them are near their destination,
   * if enough of them are then we remove the descendant bubbles from the canvas and render the ancestor bubble
   */
  private handleBubbleAmalgamating(
    amalgamatedBubble: XYBubbleMetadata,
    descendantBubbles: XYBubbleMetadata[],
  ) {
    amalgamatedBubble.x = amalgamatedBubble.destinationPosition.x
    amalgamatedBubble.y = amalgamatedBubble.destinationPosition.y

    descendantBubbles.forEach((descendant: XYBubbleMetadata) => {
      descendant.destinationPosition = amalgamatedBubble.destinationPosition
    })

    this.pendingBubbleAmalgamations.push({
      ancestor: amalgamatedBubble,
      descendants: descendantBubbles,
    })
    this.pendingBubblesToRemove.push(...descendantBubbles)
  }

  private amalgamateBubbles(bubbleAmalgamation: BubbleAmalgamation) {
    this.pendingBubbleAmalgamations = this.pendingBubbleAmalgamations.filter(
      (x) => x.ancestor !== bubbleAmalgamation.ancestor,
    )

    this.addBubblesToCanvas([bubbleAmalgamation.ancestor])
    this.removeBubblesFromCanvas(bubbleAmalgamation.descendants)

    this.updateSimulation()
  }

  private updateSimulation() {
    this.simulation.updateSimulation(
      this.metadataRegister.bubbles,
      this.config.yAxisTicks.useDistinctValues,
    )
  }

  private addBubblesToCanvas(bubbles: XYBubbleMetadata[]) {
    bubbles.forEach((bubble: XYBubbleMetadata) => {
      const bubbleGroup = new XYBubbleGroup(bubble)
      this.addGroup(bubbleGroup)

      // When a simulation is already running and Bubbles are waiting to
      // amalgamate we may already have a Bubble rendered for the given path,
      // we need to ensure that it is removed before attempting to render the new bubble
      this.removeBubblesFromCanvas([bubble])

      this.metadataRegister.addBubble(bubble)
      this.entitiesRegister.addEntity(bubbleGroup)
    })
  }

  private removeBubblesFromCanvas(bubbles: XYBubbleMetadata[]) {
    bubbles.forEach((bubble: XYBubbleMetadata) => {
      const bubbleGroup = this.entitiesRegister.getEntity(XYBubbleGroup, bubble.key)
      if (bubbleGroup) {
        this.removeGroup(bubbleGroup)
        this.entitiesRegister.removeEntity(bubbleGroup)
      }

      this.metadataRegister.removeBubble(bubble.key)
    })
  }

  private updateLayout() {
    const layout = xyBubblesLayoutCalculator.calculate(
      this.config,
      this.dimensionsMappings,
      this.simulationEndedBubbles,
      this.initialViewBoundingRect,
      this.minimumCanvasSideLength,
    )

    this.xAxis.update(layout.xAxis)
    this.yAxis.update(layout.yAxis)

    this.axesBoundingBox.update(layout.axesBoundingRect)
    this.bubblesBoundingBox.update(layout.bubblesBoundingRect)
    this.visualisationBoundingBox.update(layout.visualisationBoundingRect)

    this.updateAxesHorizontalOffsets()
    this.updateSimulation()
  }

  updateAxesHorizontalOffsets() {
    const getShapeWidth = (shape: Konva.Shape) =>
      (shape.width() * shape.getAbsoluteScale().x) / this.scale

    const getShapeLeft = (shape: Konva.Shape) => shape.absolutePosition().x / this.scale

    const viewLeft = getShapeLeft(this.currentViewBoundingBox)
    const viewRight = getShapeWidth(this.currentViewBoundingBox)

    const xAxisLineLeft = getShapeLeft(this.axesBoundingBox)
    const xAxisLineRight = xAxisLineLeft + getShapeWidth(this.axesBoundingBox)
    const xAxisLineWidth = getShapeWidth(this.axesBoundingBox)

    const visibleLineStart = Math.max(xAxisLineLeft, 0)
    const visibleLineEnd = Math.max(Math.min(xAxisLineRight, viewRight), 0)
    const visibleLineWidth = visibleLineEnd - visibleLineStart

    // X Axis Offset
    let xAxisLabelOffset = visibleLineWidth / 2
    if (xAxisLineLeft < viewLeft) {
      xAxisLabelOffset += viewLeft - xAxisLineLeft
    }
    const labelWidthOffset = this.xAxis.labelWidth / 2
    xAxisLabelOffset = _.clamp(
      xAxisLabelOffset,
      labelWidthOffset,
      xAxisLineWidth - labelWidthOffset,
    )
    this.xAxis.updateAxisLabelHorizontalOffset(-xAxisLabelOffset)

    // Y Axis Offset
    const yAxisLeftMargin = this.isMobile ? 20 : 20 / this.scale
    const yAxisLeft = this.yAxis.minTickAbsoluteLeft / this.scale
    let yAxisOffset = 0
    if (yAxisLeft < viewLeft + yAxisLeftMargin) {
      yAxisOffset = Math.max(yAxisLeft - viewLeft - yAxisLeftMargin, -xAxisLineWidth)
    }
    this.yAxis.updateHorizontalOffset(yAxisOffset)
  }
}
</script>
